import { useEffect, useState, type ReactNode } from 'react'
import * as Accordion from '@radix-ui/react-accordion'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ContextGap, GapPriority } from '@/types/api'
import { isRecheckPending, recheckExpiresAt } from './partitionGaps'

interface GapRowProps {
  gap: ContextGap
  resolved: boolean
  children: ReactNode
}

const SEVERITY_DOT: Record<GapPriority, string> = {
  critical: 'bg-error',
  high: 'bg-warning',
  medium: 'bg-info',
  low: 'bg-text-tertiary',
}

/**
 * Single accordion row. Header carries the severity dot, gap title (with
 * description fallback when title is missing — open Vidit flag), domain
 * tag pill, and chevron. Body expands inline below the header. Resolved
 * rows render with strike-through + muted styling per spec.
 */
export function GapRow({ gap, resolved, children }: GapRowProps) {
  const priority = (gap.priority ?? 'medium') as GapPriority
  const title = gap.title?.trim() || gap.description || 'Open gap'
  const checking = useRecheckPending(gap) && !resolved

  return (
    <Accordion.Item
      value={gap.id}
      data-testid={`gap-row-${gap.id}`}
      className={cn(
        'rounded-lg border bg-bg-primary transition-colors',
        resolved
          ? 'border-success/40 opacity-70'
          : 'border-border-default hover:border-border-hover data-[state=open]:border-accent'
      )}
    >
      <Accordion.Header className="flex">
        <Accordion.Trigger
          className={cn(
            'group flex items-center gap-3 w-full px-4 py-3 text-left',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-lg'
          )}
        >
          <span
            aria-hidden
            className={cn(
              'w-2 h-2 rounded-full shrink-0',
              SEVERITY_DOT[priority]
            )}
          />
          <span className="flex-1 min-w-0">
            <span
              className={cn(
                'block text-sm font-display font-semibold text-text-primary truncate',
                resolved && 'line-through text-text-tertiary'
              )}
            >
              {title}
            </span>
            {gap.description && gap.description !== title && (
              <span className="block text-xs text-text-tertiary mt-0.5 truncate">
                {gap.description}
              </span>
            )}
            {checking && <CheckingBadge />}
          </span>
          {gap.domain && (
            <span className="hidden sm:inline-flex shrink-0 items-center px-2 py-0.5 rounded font-mono text-xs text-text-tertiary bg-bg-elevated border border-border-default">
              {humanizeDomain(gap.domain)}
            </span>
          )}
          <ChevronDown
            size={16}
            className="text-text-tertiary shrink-0 transition-transform group-data-[state=open]:rotate-180"
          />
        </Accordion.Trigger>
      </Accordion.Header>
      {/* Lazy-mount: Radix only renders Content when the item is open.
          With dozens of rows, eager-mounting every body would mount N×
          GapChat (with auto-grow effect), GapPerGapUpload, and
          GapPreFillCards on first paint. */}
      <Accordion.Content
        className="overflow-hidden border-t border-border-default"
      >
        <div className="p-4 flex flex-col gap-4">{children}</div>
      </Accordion.Content>
    </Accordion.Item>
  )
}

/**
 * Whether the row should say it's being checked. Re-renders once when the
 * 15-minute window runs out, so a check that never reports back doesn't
 * leave the badge up while the page sits idle.
 */
function useRecheckPending(gap: ContextGap): boolean {
  const [, setExpiredTick] = useState(0)
  const expiresAt = recheckExpiresAt(gap)
  const pending = isRecheckPending(gap)

  useEffect(() => {
    if (!pending || expiresAt === null) return
    const id = window.setTimeout(
      () => setExpiredTick((n) => n + 1),
      expiresAt - Date.now()
    )
    return () => window.clearTimeout(id)
  }, [pending, expiresAt])

  return pending
}

/**
 * Shown while the system re-checks this gap against newly added material.
 * Sits under the title so it stays readable at phone width. Static dot —
 * DESIGN.md §Motion reserves animation for the signature moments.
 */
function CheckingBadge() {
  return (
    <span
      data-testid="gap-checking-badge"
      className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-[color:var(--blue-glow-strong)] bg-[var(--blue-glow)] px-2 py-0.5 text-xs text-text-secondary"
    >
      <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-primary" />
      Checking what you added…
    </span>
  )
}

function humanizeDomain(domain: string): string {
  return domain.replace(/_/g, ' ')
}
